import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { adminAPI, maintenanceAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import TwoFactorModal from '../components/TwoFactorModal';
import use2faProtect from '../hooks/use2faProtect';

const AdminMaintenance = () => {
  const { protect, show2fa, error2fa, handleVerify, handleClose } = use2faProtect();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeUsers, setActiveUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [form, setForm] = useState({
    title: '',
    message: '',
    start_time: '',
    end_time: '',
    notify_users: true,
  });

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        maintenanceAPI.getStatus(),
        adminAPI.getMaintenanceHistory(),
      ]);
      if (statusRes.data?.success) setStatus(statusRes.data.data);
      if (historyRes.data?.success) setHistory(historyRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load maintenance data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActiveUsers = useCallback(async () => {
    try {
      const res = await adminAPI.getActiveUsers();
      if (res.data?.success) setActiveUsers(res.data.data);
    } catch {
      // Non-critical - endpoint may not exist on older backends
    }
  }, []);

  useEffect(() => { fetchData(); fetchActiveUsers(); }, [fetchData, fetchActiveUsers]);

  // Auto-refresh status every 15 seconds when maintenance is active
  useEffect(() => {
    if (!status?.maintenance_mode && !status?.schedule) return;
    const interval = setInterval(fetchData, 15 * 1000);
    return () => clearInterval(interval);
  }, [status?.maintenance_mode, status?.schedule, fetchData]);

  const handleSchedule = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await adminAPI.scheduleMaintenance({
        ...form,
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
      });
      if (res.data?.success) {
        toast.success('Maintenance scheduled successfully');
        setShowScheduleForm(false);
        setForm({ title: '', message: '', start_time: '', end_time: '', notify_users: true });
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to schedule maintenance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel the active maintenance schedule?')) return;
    try {
      await adminAPI.cancelMaintenance();
      toast.success('Maintenance cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel');
    }
  };

  const handleActivateNow = async () => {
    if (!window.confirm('Activate maintenance mode NOW? All non-admin users will be logged out.')) return;
    setSubmitting(true);
    try {
      await protect(async (headers) => {
        const res = await adminAPI.activateMaintenanceNow({
          title: 'Emergency Maintenance',
          message: 'System is under emergency maintenance. Please try again later.',
          duration_minutes: 60,
        }, headers);
        const loggedOut = res.data?.data?.users_logged_out;
        toast.success(`Maintenance mode activated${loggedOut ? `. ${loggedOut} user(s) logged out.` : ''}`);
        fetchData();
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to activate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnd = async () => {
    if (!window.confirm('End maintenance mode and restore access?')) return;
    try {
      const res = await adminAPI.endMaintenance();
      toast.success(res.data?.message || 'Maintenance ended - system is back online');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to end maintenance');
    }
  };

  const handleForceLogoutAll = async () => {
    if (!window.confirm('Force logout ALL non-admin users? This cannot be undone.')) return;
    try {
      await protect(async (headers) => {
        const res = await adminAPI.forceLogoutAll(headers);
        toast.success(res.data?.message || 'All users logged out');
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  // Derive the real lifecycle status from backend data
  const scheduleStatus = status?.schedule?.status;
  const isRunning = status?.maintenance_mode || scheduleStatus === 'running';
  const isScheduled = !isRunning && !!status?.schedule && scheduleStatus !== 'completed' && scheduleStatus !== 'cancelled';
  const isOnline = !isRunning && !isScheduled;

  return (
    <AdminLayout>
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.title}>Maintenance Management</h1>
          <p style={styles.subtitle}>Schedule maintenance, manage downtime, and control user sessions</p>
        </div>

        {/* Active Users Panel (Pre-maintenance decision support) */}
        {activeUsers && (
          <div style={{
            ...styles.card,
            borderLeft: `4px solid ${activeUsers.total_active > 0 ? '#FD7E14' : '#28A745'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h2 style={styles.cardTitle}>Active Users</h2>
                <p style={{ color: '#6C757D', margin: '0 0 4px', fontSize: '13px' }}>
                  Users active in the last {activeUsers.threshold_minutes} minutes
                </p>
              </div>
              <div style={{
                fontSize: '32px',
                fontWeight: 700,
                color: activeUsers.total_active > 0 ? '#FD7E14' : '#28A745',
              }}>
                {activeUsers.total_active}
              </div>
            </div>
            {activeUsers.total_active > 0 && activeUsers.users?.length > 0 && (
              <div style={{ marginTop: 12, maxHeight: 150, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={styles.thSmall}>User</th>
                      <th style={styles.thSmall}>Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.users.slice(0, 10).map((u) => (
                      <tr key={u.user_id}>
                        <td style={styles.tdSmall}>{u.full_name || u.email}</td>
                        <td style={styles.tdSmall}>{new Date(u.last_active_at).toLocaleTimeString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {activeUsers.users.length > 10 && (
                  <p style={{ fontSize: 12, color: '#6C757D', marginTop: 4 }}>
                    ...and {activeUsers.users.length - 10} more
                  </p>
                )}
              </div>
            )}
            <button
              onClick={fetchActiveUsers}
              style={{ ...styles.btnSecondary, marginTop: 12, padding: '6px 14px', fontSize: '13px' }}
            >
              Refresh
            </button>
          </div>
        )}

        {/* Current Status Card */}
        <div style={{
          ...styles.card,
          borderLeft: `4px solid ${isRunning ? '#DC3545' : isScheduled ? '#FFC107' : '#28A745'}`,
        }}>
          <h2 style={styles.cardTitle}>Current Status</h2>
          {loading ? (
            <p>Loading...</p>
          ) : isRunning ? (
            <div>
              <div style={styles.statusBadge('#DC3545')}>UNDER MAINTENANCE</div>
              <p style={styles.statusDetail}><strong>Title:</strong> {status.schedule?.title}</p>
              <p style={styles.statusDetail}><strong>Message:</strong> {status.schedule?.message}</p>
              <p style={styles.statusDetail}>
                <strong>Planned End:</strong> {new Date(status.schedule?.end_time).toLocaleString()}
              </p>
              {status.schedule?.actual_start_time && (
                <p style={styles.statusDetail}>
                  <strong>Started At:</strong> {new Date(status.schedule.actual_start_time).toLocaleString()}
                </p>
              )}
              <button onClick={handleEnd} style={styles.btnSuccess}>End Maintenance</button>
            </div>
          ) : isScheduled ? (
            <div>
              <div style={styles.statusBadge('#FFC107')}>SCHEDULED</div>
              <p style={styles.statusDetail}><strong>Title:</strong> {status.schedule?.title}</p>
              <p style={styles.statusDetail}><strong>Starts:</strong> {new Date(status.schedule?.start_time).toLocaleString()}</p>
              <p style={styles.statusDetail}><strong>Ends:</strong> {new Date(status.schedule?.end_time).toLocaleString()}</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={handleCancel} style={styles.btnDanger}>Cancel Schedule</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={styles.statusBadge('#28A745')}>SYSTEM ONLINE</div>
              <p style={{ color: '#6C757D', marginTop: 8 }}>No maintenance scheduled. System is operating normally.</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Actions</h2>
          <div style={styles.actionGrid}>
            {isOnline && (
              <button onClick={() => setShowScheduleForm(true)} style={styles.actionBtn}>
                Schedule Maintenance
              </button>
            )}
            {!isRunning && (
              <button onClick={handleActivateNow} style={{ ...styles.actionBtn, ...styles.btnDangerOutline }} disabled={submitting}>
                Activate Maintenance Now
              </button>
            )}
            <button onClick={handleForceLogoutAll} style={{ ...styles.actionBtn, ...styles.btnWarningOutline }}>
              Force Logout All Users
            </button>
          </div>
        </div>

        {/* Schedule Form */}
        {showScheduleForm && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Schedule Maintenance</h2>
            <form onSubmit={handleSchedule} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Title *</label>
                <input
                  style={styles.input}
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Database Migration"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Message to Users</label>
                <textarea
                  style={{ ...styles.input, minHeight: 80 }}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="System is under scheduled maintenance. Please try again later."
                />
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ ...styles.formGroup, flex: 1, minWidth: 200 }}>
                  <label style={styles.label}>Start Time *</label>
                  <input
                    style={styles.input}
                    type="datetime-local"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    required
                  />
                </div>
                <div style={{ ...styles.formGroup, flex: 1, minWidth: 200 }}>
                  <label style={styles.label}>End Time *</label>
                  <input
                    style={styles.input}
                    type="datetime-local"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.notify_users}
                    onChange={(e) => setForm({ ...form, notify_users: e.target.checked })}
                  />
                  Notify users before maintenance starts
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="submit" style={styles.btnPrimary} disabled={submitting}>
                  {submitting ? 'Scheduling...' : 'Schedule Maintenance'}
                </button>
                <button type="button" style={styles.btnSecondary} onClick={() => setShowScheduleForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* History */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Maintenance History</h2>
          {history.length === 0 ? (
            <p style={{ color: '#6C757D' }}>No maintenance history yet.</p>
          ) : (
            <div style={styles.table}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Title</th>
                    <th style={styles.th}>Planned</th>
                    <th style={styles.th}>Actual</th>
                    <th style={styles.th}>Duration</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Logged Out</th>
                    <th style={styles.th}>Created By</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => {
                    const itemStatus = resolveHistoryStatus(item);
                    const statusColor = STATUS_COLORS[itemStatus] || '#6C757D';
                    const statusLabel = STATUS_LABELS[itemStatus] || itemStatus;

                    const plannedStart = new Date(item.start_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
                    const plannedEnd = new Date(item.end_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });

                    const actualStart = item.actual_start_time
                      ? new Date(item.actual_start_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
                      : null;
                    const actualEnd = item.actual_end_time
                      ? new Date(item.actual_end_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
                      : null;

                    const hasActualDiff = actualStart || actualEnd;

                    const durationText = item.actual_duration_minutes != null
                      ? `${item.actual_duration_minutes} min`
                      : null;

                    return (
                      <tr key={item.id}>
                        <td style={styles.td}>{item.title}</td>
                        <td style={styles.td}>
                          <span style={{ fontSize: 12 }}>{plannedStart}</span>
                          <br />
                          <span style={{ fontSize: 12, color: '#6C757D' }}>{plannedEnd}</span>
                        </td>
                        <td style={styles.td}>
                          {hasActualDiff ? (
                            <>
                              <span style={{ fontSize: 12 }}>{actualStart || '-'}</span>
                              <br />
                              <span style={{ fontSize: 12, color: '#6C757D' }}>{actualEnd || 'ongoing'}</span>
                            </>
                          ) : (
                            <span style={{ fontSize: 12, color: '#ADB5BD' }}>-</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          {durationText ? (
                            <span style={{ fontSize: 12 }}>
                              {durationText}
                              {item.ended_early && (
                                <span style={{ color: '#28A745', fontSize: 11, marginLeft: 4 }}>early</span>
                              )}
                            </span>
                          ) : (
                            <span style={{ fontSize: 12, color: '#ADB5BD' }}>-</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, backgroundColor: statusColor }}>
                            {statusLabel}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontSize: 12 }}>
                            {item.users_logged_out != null && item.users_logged_out > 0
                              ? item.users_logged_out
                              : '-'}
                          </span>
                        </td>
                        <td style={styles.td}>{item.created_by_email || 'System'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <TwoFactorModal
        isOpen={show2fa}
        onClose={handleClose}
        onVerify={handleVerify}
        error={error2fa}
      />
    </AdminLayout>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  scheduled: '#FFC107',
  running: '#DC3545',
  completed: '#28A745',
  cancelled: '#6C757D',
};

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  running: 'Running',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** Resolve history item status from backend fields (backward compatible). */
function resolveHistoryStatus(item) {
  // If backend sends the status column, use it directly
  if (item.status && STATUS_COLORS[item.status]) {
    return item.status;
  }
  // Fallback: derive from legacy fields
  if (item.cancelled_by) return 'cancelled';
  if (item.is_active && item.activated_at) return 'running';
  if (item.is_active) return 'scheduled';
  return 'completed';
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  page: { padding: '24px', maxWidth: '960px', width: '100%', boxSizing: 'border-box', minWidth: 0, overflow: 'hidden' },
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: 700, color: 'var(--color-text)', margin: '0 0 4px' },
  subtitle: { color: 'var(--color-text-secondary)', margin: 0, fontSize: '14px' },
  card: {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden',
    minWidth: 0,
  },
  cardTitle: { fontSize: '18px', fontWeight: 600, margin: '0 0 16px', color: 'var(--color-text)' },
  statusBadge: (color) => ({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 700,
    color: '#FFF',
    backgroundColor: color,
    letterSpacing: '0.5px',
  }),
  statusDetail: { color: 'var(--color-text-secondary)', margin: '8px 0', fontSize: '14px' },
  actionGrid: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  actionBtn: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: '1px solid #0D6EFD',
    backgroundColor: '#0D6EFD',
    color: '#FFF',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '14px',
  },
  btnDangerOutline: {
    backgroundColor: 'transparent',
    border: '1px solid #DC3545',
    color: '#DC3545',
  },
  btnWarningOutline: {
    backgroundColor: 'transparent',
    border: '1px solid #FD7E14',
    color: '#FD7E14',
  },
  btnPrimary: {
    padding: '10px 24px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#0D6EFD',
    color: '#FFF',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnSecondary: {
    padding: '10px 24px',
    borderRadius: '6px',
    border: '1px solid #6C757D',
    backgroundColor: 'transparent',
    color: '#6C757D',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnSuccess: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#28A745',
    color: '#FFF',
    cursor: 'pointer',
    fontWeight: 500,
    marginTop: '12px',
  },
  btnDanger: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#DC3545',
    color: '#FFF',
    cursor: 'pointer',
    fontWeight: 500,
  },
  form: {},
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontWeight: 500, marginBottom: '6px', fontSize: '14px', color: 'var(--color-text-secondary)' },
  input: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid var(--color-border)',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--color-bg-subtle)',
    color: 'var(--color-text)',
  },
  table: { overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%', display: 'block' },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '2px solid var(--color-border)',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid var(--color-border-light)',
    fontSize: '13px',
    color: 'var(--color-text)',
    verticalAlign: 'top',
  },
  thSmall: {
    textAlign: 'left',
    padding: '6px 8px',
    borderBottom: '1px solid #DEE2E6',
    fontSize: '11px',
    fontWeight: 600,
    color: '#6C757D',
  },
  tdSmall: {
    padding: '6px 8px',
    borderBottom: '1px solid #E9ECEF',
    fontSize: '12px',
    color: '#495057',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#FFF',
  },
};

export default AdminMaintenance;
